import { type StrategyVersion, parseUserStrategyRef } from "@trader/shared/StrategiesApi"
import { ApiError, call } from "../../api.ts"
import { loadDocument } from "../../documents.ts"
import { draftDocumentKey, draftKeyOf, NEW_DRAFT, parseDraft } from "../strategy-editor/draftDocument.ts"
import { userDrafts } from "./userDrafts.ts"
import type { ScriptCode } from "./userScriptProtocol.ts"

// What a pane's user strategy runs (docs/design/0005, P4):
//   "user:<id>@<v>"  that version's compiled JS (versions are immutable: cached)
//   "user:<id>"      the latest version's
//   "draft:<key>"    the editor's draft source: as published to userDrafts, else
//                    as autosaved, else (a saved strategy's cleared draft) its latest version

export interface UserScript {
  readonly name: string
  readonly code: ScriptCode
}

/** The script can't be found (deleted draft, another user's or unknown strategy). */
export class ScriptUnavailable extends Error {}

const versions = new Map<string, Promise<StrategyVersion>>()
/** The signed-in user the cache belongs to (userDrafts.generation). */
let cachedFor = userDrafts.generation()

const unavailable = (error: unknown): never => {
  if (error instanceof ApiError && error.tag === "StrategyNotFound") {
    throw new ScriptUnavailable("this strategy isn't available: it was removed, or it belongs to another user.")
  }
  throw error
}

const fromVersion = (version: StrategyVersion): UserScript => ({
  name: version.declaration.title,
  code: { kind: "compiled", js: version.compiled }
})

/** A saved version, fetched once (a failed fetch is retried next time). */
export const loadVersion = (strategyId: string, version: number): Promise<StrategyVersion> => {
  if (cachedFor !== userDrafts.generation()) {
    versions.clear()
    cachedFor = userDrafts.generation()
  }
  const key = `${strategyId}@${version}`
  let loading = versions.get(key)
  if (loading === undefined) {
    const ref = parseUserStrategyRef(`user:${strategyId}@${version}`)
    if (ref === null) return Promise.reject(new ScriptUnavailable(`"${strategyId}" isn't a strategy id.`))
    loading = call((client) => client.strategies.version({ params: { id: ref.strategyId, version } }))
    loading.catch(() => {
      if (versions.get(key) === loading) versions.delete(key)
    })
    versions.set(key, loading)
  }
  return loading
}

const latest = async (strategyId: string): Promise<UserScript> => {
  const ref = parseUserStrategyRef(`user:${strategyId}`)
  if (ref === null) throw new ScriptUnavailable(`"${strategyId}" isn't a strategy id.`)
  const detail = await call((client) => client.strategies.get({ params: { id: ref.strategyId } })).catch(unavailable)
  return fromVersion(detail.latest)
}

export const resolveUserScript = async (strategyId: string): Promise<UserScript> => {
  const draftKey = draftKeyOf(strategyId)
  if (draftKey !== null) {
    const published = userDrafts.get(draftKey)
    if (published !== undefined) return { name: published.name, code: { kind: "source", source: published.source } }
    const stored = parseDraft(await loadDocument(draftDocumentKey(draftKey)))
    if (stored !== null) return { name: stored.name, code: { kind: "source", source: stored.source } }
    if (draftKey !== NEW_DRAFT) return latest(draftKey)
    throw new ScriptUnavailable("this draft is gone. Open the strategy editor to write it again.")
  }
  const ref = parseUserStrategyRef(strategyId)
  if (ref === null) throw new ScriptUnavailable(`unknown strategy ${strategyId}`)
  if (ref.version === null) return latest(ref.strategyId)
  return fromVersion(await loadVersion(ref.strategyId, ref.version).catch(unavailable))
}
